const { getUnknownBatchID } = require('../shipment/tools/virtual-batch')
const { ZONE_CATEGORIES } = require('./constants')

const DEFAULT_BATCH_NUMBER = 'UNKNOWN'

function getZoneCategory (zone) {
  if (!zone) return undefined
  return ZONE_CATEGORIES[String(zone).toUpperCase()]
}

function isSellableZone (zone) {
  return getZoneCategory(zone) === 'sellable'
}

function normalizeBatchNumber (batch) {
  if (batch === null || typeof batch === 'undefined' || batch === '') {
    return DEFAULT_BATCH_NUMBER
  }
  return String(batch)
}

function formatSkuBatchAggregateKey (skuCode, batchNo = DEFAULT_BATCH_NUMBER) {
  return JSON.stringify([String(skuCode), normalizeBatchNumber(batchNo)])
}

function parseSkuBatchAggregateKey (aggregateKey) {
  const [skuCode, batchNumber] = JSON.parse(aggregateKey)
  if (!skuCode) {
    return { skuCode: null, batchNumber: DEFAULT_BATCH_NUMBER }
  }
  return { skuCode, batchNumber: normalizeBatchNumber(batchNumber) }
}

function parseCountKey (countKey = '') {
  const skuMatch = countKey.match(/^product:([^:]+)/i)
  const manufacturerMatch = countKey.match(/:manufacturer:([^:]+)/i)
  const batchMatch = countKey.match(/:batchNo:([^:]+)/i)
  return {
    skuCode: skuMatch && skuMatch[1],
    manufacturer: manufacturerMatch && manufacturerMatch[1],
    batchNo: batchMatch ? batchMatch[1] : DEFAULT_BATCH_NUMBER
  }
}

function buildCountKey (skuCode, manufacturer = 'unknown', batchNo = DEFAULT_BATCH_NUMBER) {
  return `product:${skuCode}:manufacturer:${manufacturer}:batchNo:${normalizeBatchNumber(batchNo)}`
}

function joinRemarks (remarksList) {
  return (remarksList || [])
    .filter(Boolean)
    .join(', ')
}

// Key builders
function keyUnknownBatch (skuCode) {
  return getUnknownBatchID(skuCode)
}

function keySkuSupplier (skuCode, supplierRef) {
  return `product:${skuCode}:manufacturer:${supplierRef}`
}

function slug (s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\s+/g, '_')
}

function keySkuBatch (skuDesc, batchNo) {
  return `product:${slug(skuDesc)}:manufacturer:UNKNOWN:batchNo:${batchNo}`
}

// Quantity selectors
function qtyRtv (item) {
  return (item.rtv_quantity || 0)
}

/**
 * Extract country code from location ID
 * @param {string} locationId - Location ID in format 'country:XX:state:YY' or similar
 * @returns {string|null} - Country code (e.g., 'ng', 'ke') or null if not found
 */
function extractCountryCode (locationId) {
  if (!locationId || typeof locationId !== 'string') {
    return null
  }

  // Match pattern like 'country:ng:state:fct' or 'country:ke:state:nairobi'
  const match = locationId.match(/^country:([a-z]{2})/)
  return match ? match[1] : null
}

/**
 * Sum counts for either flat list of items or list of entries with items.
 * options: { nested: boolean, key: (item)=>string, qty: (item)=>number, enforceNonNull: boolean }
 */
function sumCounts (source, { nested = false, key, qty, enforceNonNull = false } = {}) {
  const counts = {}
  const eachItem = (item) => {
    if (!item) {
      if (enforceNonNull) {
        throw new Error('undefined-item-inside-rtv')
      }
      return
    }
    const k = key(item)
    const amount = qty(item)
    if (!counts[k]) counts[k] = { quantity: 0 }
    counts[k].quantity += amount
  }

  if (nested) {
    (source || []).forEach(entry => (entry.items || []).forEach(eachItem))
  } else {
    (source || []).forEach(eachItem)
  }
  return counts
}

// Aggregate the numerical quantities per (sku,batch) combination and
function aggregateQuantitiesBySku (items = [], qtySelector = (() => 0), options = {}) {
  const totals = {}
  const { getBatchEntries } = options

  items.forEach(item => {
    if (!item || typeof item.sku_code === 'undefined') {
      return
    }
    const skuCode = String(item.sku_code)
    const baseQty = Number(qtySelector(item))
    if (!Number.isFinite(baseQty)) {
      return
    }

    const batchEntries = typeof getBatchEntries === 'function' ? getBatchEntries(item) : null
    if (Array.isArray(batchEntries) && batchEntries.length > 0) {
      batchEntries.forEach(batchEntry => {
        if (!batchEntry) return
        const qtyFromBatch = Number(
          typeof batchEntry.quantity !== 'undefined' ? batchEntry.quantity : baseQty
        )

        if (!Number.isFinite(qtyFromBatch)) return

        const batchNumber = batchEntry.batchNumber
        const key = formatSkuBatchAggregateKey(skuCode, batchNumber)
        totals[key] = (totals[key] || 0) + qtyFromBatch
      })
      return
    }

    const fallbackKey = formatSkuBatchAggregateKey(skuCode, DEFAULT_BATCH_NUMBER)
    totals[fallbackKey] = (totals[fallbackKey] || 0) + baseQty
  })
  return totals
}

// Rewrites shipment counts with the new per-batch totals, cloning metadata.
function updateCountsFromSkuQuantities (counts, skuBatchQuantities = {}) {
  const baseCounts = counts || {}
  const entriesBySku = {}

  Object.entries(baseCounts).forEach(([countKey, entry]) => {
    const parsed = parseCountKey(countKey)
    if (!parsed.skuCode) {
      return
    }
    if (!entriesBySku[parsed.skuCode]) {
      entriesBySku[parsed.skuCode] = []
    }
    entriesBySku[parsed.skuCode].push({ ...parsed, entry })
  })

  const nextCounts = {}

  // Build lookup tables from the current shipment counts so we can preserve
  // paymentType even for newly introduced batches/SKUs.
  const skuPaymentType = {}
  let defaultPaymentType = null
  Object.entries(baseCounts).forEach(([countKey, entry]) => {
    const paymentType = entry && entry.paymentType
    if (!paymentType) return
    if (!defaultPaymentType) {
      defaultPaymentType = paymentType
    }
    const { skuCode } = parseCountKey(countKey)
    if (skuCode && !skuPaymentType[skuCode]) {
      skuPaymentType[skuCode] = paymentType
    }
  })

  Object.entries(skuBatchQuantities).forEach(([aggregateKey, quantity]) => {
    const { skuCode, batchNumber } = parseSkuBatchAggregateKey(aggregateKey)
    if (!skuCode) {
      return
    }

    const skuEntries = entriesBySku[skuCode] || []
    const normalizedBatch = normalizeBatchNumber(batchNumber)
    const templateEntry = skuEntries.find(e => normalizeBatchNumber(e.batchNo) === normalizedBatch) || skuEntries[0]

    const manufacturer = (templateEntry && templateEntry.manufacturer) || 'unknown'

    const templateFields = templateEntry && templateEntry.entry
      ? templateEntry.entry
      : {}

    const newKey = buildCountKey(skuCode, manufacturer, batchNumber)
    const nextCount = {
      ...templateFields,
      quantity
    }

    // If the template did not carry paymentType (or there was no template),
    // infer it from existing shipment counts:
    // 1) Prefer SKU-specific paymentType
    // 2) Fallback to shipment-level default paymentType
    if (!nextCount.paymentType) {
      const inferredPaymentType = skuPaymentType[skuCode] || defaultPaymentType
      if (inferredPaymentType) {
        nextCount.paymentType = inferredPaymentType
      }
    }

    nextCounts[newKey] = nextCount
  })

  return nextCounts
}

module.exports = {
  joinRemarks,
  keyUnknownBatch,
  keySkuSupplier,
  keySkuBatch,
  qtyRtv,
  sumCounts,
  extractCountryCode,
  aggregateQuantitiesBySku,
  updateCountsFromSkuQuantities,
  getZoneCategory,
  isSellableZone
}
